import { useMemo } from 'react';
import DeliveryLocationCellRenderer from '../../../components/cellRenderers/DeliveryLocationCellRenderer';
import TradingCompanyCodeCellRenderer from '../../../components/cellRenderers/TradingCompanyCodeCellRenderer';
import useExcelStyles from '../useExcelStyles';
import useNoRowsTemplate from '../useNoRowsOverlay';
import useLoadingOverlay from '../useLoadingOverlay';
import moment from 'moment';
import { TOOLTIP_SHOW_DELAY } from '../../../constants';
import store from '../../../redux/store';
import type { MillSheetGridData, MillSheetSource } from '../../../types/millSheetSearch';
import type { ProcessCellCallbackParams } from '../../../types/common';
import type { ExcelStyle, GetContextMenuItemsParams, GridOptions, MenuItemDef } from 'ag-grid-community';

const useGridOptionsManageMillSheetSearch = () => {
  const { commonExcelStyles } = useExcelStyles();

  // Excelのスタイル設定
  // ----------------------------------------
  // cellClass と id を一致させる。dataType: 'string' で Excel 上も文字列として出力する（科学的記数法化を防ぐ）。
  // 付与する列は colDef 側で制御する（例: 契約番号は従来どおり文字列出力、溶解番号は TM-177 で追加）。
  // @ts-ignore https://www.ag-grid.com/react-data-grid/excel-export-styles/
  const excelStringColumnStyle: ExcelStyle = useMemo(() => ({
    id: 'excelStringColumn',
    dataType: 'string',
    // @ts-ignore
    font: {
      fontName: 'MeiryoUI',
      size: 9,
      // color: '#0000ff',
    },
  }), []);
  const excelStyles: ExcelStyle[] = useMemo<ExcelStyle[]>(() => {
    return [...commonExcelStyles, excelStringColumnStyle];
  }, [commonExcelStyles, excelStringColumnStyle]);

  // Overlayの設定
  // ----------------------------------------
  const overlayLoadingTemplate: string = useLoadingOverlay();
  const overlayNoRowsTemplate: string = useNoRowsTemplate();

  // Rendererの設定
  // ----------------------------------------
  const frameworkComponents = {
    deliveryLocationCellRenderer: DeliveryLocationCellRenderer,
    tradingCompanyCellRenderer: TradingCompanyCodeCellRenderer,
  };

  // rowNodeIdの設定
  // ----------------------------------------
  const getRowNodeId = (params: MillSheetGridData): string => params._id;

  // 右クリックメニューの設定
  // ----------------------------------------
  const getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    // console.log(params);
    if (params.node) {
      // Excel用の変換を行う
      const processCellCallback = (params: ProcessCellCallbackParams): string => {
        // console.log(params.value);
        const myField: string | undefined = params.column.getColDef().field;
        if (myField === '証明書発行年月日') {
          const value: string = params.value;
          const formattedValue: string | undefined = moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : value;
          // console.log(formattedValue);
          return formattedValue;
        } else if (myField === '受渡場所名') {
          // DeliveryLocationCellRendererと同じロジックを適用
          // processCellCallback内で直接Reduxストアから最新の値を取得
          const state = store.getState();
          const deliveryLocations = state.deliveryLocation.deliveryLocations;
          const data: MillSheetSource | undefined = params.node.data;
          const deliveryLocationCode: string | undefined = data?.['受渡場所コード'];
          const deliveryLocationName: string | undefined | null = params.value;
          // 受渡場所一覧が取得できており、かつ受渡場所コードが存在する場合は、受渡場所コードから受渡場所名を解決
          if (deliveryLocations !== undefined && deliveryLocationCode) {
            const name: string | undefined =
              deliveryLocationName && deliveryLocationName !== ''
                ? // deliveryNameがあればそれをそのまま使う
                deliveryLocationName
                : // deliveryNameがなければdeliveryLocationsから該当するものを探してその名前を使う
                deliveryLocations.find((location) => location.code === deliveryLocationCode)?.deliveryName;
            return name ?? deliveryLocationName ?? '';
          }
          return deliveryLocationName ?? '';
        } else {
          return params.value ?? '';
        }
      };
      // カスタムメニューの作成
      const exportMenu = {
        icon: '<span class="ag-icon ag-icon-save" unselectable="on"></span>',
        name: 'Export',
        subMenu: [
          {
            name: 'CSV Export',
            action: () => params.api.exportDataAsCsv({ processCellCallback }),
          },
          {
            name: 'Excel Export (.xlsx)',
            action: () => params.api.exportDataAsExcel({ processCellCallback }),
          },
          // {
          //   name: 'Excel Export (.xml)',
          //   action: () => params.api.exportDataAsExcel({ exportMode: 'xml' }),
          // },
        ],
      };
      // 右クリックメニューの項目
      const menuItems = [
        // 'copy',
        // 'copyWithHeaders',
        // 'separator',
        // 'export',
        exportMenu,
      ];
      return menuItems;
    } else {
      // データrowでないエリアを右クリックしたときはメニューは表示しない
      return [];
    }
  };

  // GridOptions全体
  // ----------------------------------------
  const gridOptions: GridOptions = {
    allowContextMenuWithControlKey: true,
    alwaysShowVerticalScroll: true,
    enableRangeSelection: true,
    excelStyles,
    frameworkComponents,
    getContextMenuItems,
    getRowNodeId,
    overlayLoadingTemplate,
    overlayNoRowsTemplate,
    rowSelection: 'multiple',
    tooltipShowDelay: TOOLTIP_SHOW_DELAY,
  };
  return { gridOptions };
};

export default useGridOptionsManageMillSheetSearch;
